import type {
  BlogPost,
  BlogPostMeta,
  BlogCategory,
  BlogPostLocale,
} from "./types";

// Metadata-only imports — bodies are intentionally NOT pulled here.
// This keeps the eager bundle small; bodies load via `bodyLoader.ts`
// only when an article page mounts.
import { meta as praxiswebsite } from "./posts/praxiswebsite-was-patienten-wirklich-suchen";
import { meta as telefon } from "./posts/telefon-besetzt-anrufe-im-praxisalltag";
import { meta as terminbuchung } from "./posts/online-terminbuchung-wann-sinnvoll";
import { meta as orthopaedeTermin } from "./posts/orthopaede-termin-schnell";
import { meta as rueckenschmerzen } from "./posts/rueckenschmerzen-wann-zum-arzt";
import { meta as knieschmerzen } from "./posts/knieschmerzen-was-tun";
import { meta as facharztWartezeit } from "./posts/facharzt-termin-wartezeit-verkuerzen";
import { meta as onlineArzttermin } from "./posts/online-arzttermin-vorteile-nachteile";
import { meta as patientenanfragen } from "./posts/patientenanfragen-effizient-bearbeiten";
import { meta as terminabsagen } from "./posts/terminabsagen-reduzieren-praxis";
import { meta as telefonserviceSinnvoll } from "./posts/telefonservice-praxis-sinnvoll";
import { meta as praxisorganisation } from "./posts/praxisorganisation-verbessern";
import { meta as digitaleTerminvergabe } from "./posts/digitale-terminvergabe-praxis-vorteile";
import { meta as praxiswebsiteStruktur } from "./posts/praxiswebsite-struktur-statt-design";
import { meta as digitalisierungLeitfaden } from "./posts/digitalisierung-arztpraxis-leitfaden";
import { meta as rechtssicherePraxiswebsite } from "./posts/rechtssichere-praxiswebsite";

const allPosts: BlogPostMeta[] = [
  praxiswebsite,
  telefon,
  terminbuchung,
  orthopaedeTermin,
  rueckenschmerzen,
  knieschmerzen,
  facharztWartezeit,
  onlineArzttermin,
  patientenanfragen,
  terminabsagen,
  telefonserviceSinnvoll,
  praxisorganisation,
  digitaleTerminvergabe,
  praxiswebsiteStruktur,
  digitalisierungLeitfaden,
  rechtssicherePraxiswebsite,
] as BlogPostMeta[];

// Sorted newest first
export const posts: BlogPostMeta[] = [...allPosts].sort((a, b) =>
  b.publishedAt.localeCompare(a.publishedAt),
);

/**
 * Look up a post (metadata only) by the slug variant for a specific locale.
 * /de/blog/:slug matches against `slug.de`; /en/blog/:slug matches against `slug.en`.
 */
export function getPostBySlugForLocale(
  slug: string,
  locale: BlogLanguage,
): BlogPostMeta | undefined {
  return posts.find((p) =>
    locale === "en" ? p.slug.en === slug : p.slug.de === slug,
  );
}

/**
 * Look up a post by either its DE or EN slug. Used to resolve legacy URLs
 * (e.g. /en/blog/{de-slug}) so the page can redirect to the canonical
 * localized URL instead of 404-ing.
 */
export function getPostByAnySlug(
  slug: string,
): { post: BlogPostMeta; matchedLocale: BlogLanguage } | undefined {
  for (const p of posts) {
    if (p.slug.de === slug) return { post: p, matchedLocale: "de" };
    if (p.slug.en === slug) return { post: p, matchedLocale: "en" };
  }
  return undefined;
}

export type BlogLanguage = "de" | "en";

export function getCategoryLabel(
  category: BlogCategory,
  language: BlogLanguage = "de",
): string {
  if (language === "en") {
    switch (category) {
      case "patienten":
        return "For patients";
      case "praxen":
        return "For practices";
      case "einblicke":
        return "Insights";
    }
  }
  switch (category) {
    case "patienten":
      return "Für Patienten";
    case "praxen":
      return "Für Praxen";
    case "einblicke":
      return "Einblicke";
  }
}

/**
 * Returns the localized metadata view of a post. Falls back to the
 * German fields when an English translation is not provided. Body is
 * intentionally not part of this view — load it via `bodyLoader.ts`
 * when actually rendering the article.
 */
export function getLocalizedPost(
  post: BlogPostMeta,
  language: BlogLanguage,
): Omit<BlogPostLocale, "body"> &
  Pick<BlogPostMeta, "slug" | "category" | "publishedAt" | "updatedAt"> {
  const base = {
    title: post.title,
    excerpt: post.excerpt,
    metaTitle: post.metaTitle,
    metaDescription: post.metaDescription,
  };
  const locale = language === "en" && post.en ? post.en : base;
  return {
    slug: post.slug,
    category: post.category,
    publishedAt: post.publishedAt,
    updatedAt: post.updatedAt,
    title: locale.title,
    excerpt: locale.excerpt,
    metaTitle: locale.metaTitle,
    metaDescription: locale.metaDescription,
  };
}

export function getRelatedPosts(
  post: BlogPostMeta,
  limit = 2,
): BlogPostMeta[] {
  if (post.related && post.related.length > 0) {
    const explicit = post.related
      .map((slug) => getPostBySlugForLocale(slug, "de"))
      .filter(
        (p): p is BlogPostMeta => Boolean(p) && p!.slug.de !== post.slug.de,
      );
    if (explicit.length > 0) return explicit.slice(0, limit);
  }
  return posts
    .filter((p) => p.slug.de !== post.slug.de && p.category === post.category)
    .slice(0, limit);
}

export type {
  BlogPost,
  BlogPostMeta,
  BlogCategory,
  BlogPostLocale,
} from "./types";
